import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Camera as CameraIcon, RotateCcw, Check, X, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface CameraCaptureMultiProps {
  onCapture: (files: File[]) => void;
  onCancel: () => void;
  patientId: string | null;
}

export function CameraCaptureMulti({ onCapture, onCancel, patientId }: CameraCaptureMultiProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImages, setCapturedImages] = useState<File[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [isReady, setIsReady] = useState(false);

  const startCamera = useCallback(async (mode: "user" | "environment") => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsReady(true);
      }
      setError(null);
    } catch (err) {
      console.error("Erro ao acessar câmera:", err);
      setError(err instanceof Error ? err.message : "Erro ao acessar câmera");
    }
  }, []);

  useEffect(() => {
    startCamera(facingMode);
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "unset";
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !streamRef.current) return;

    // Validação: se já tem 1 foto, verifica se está na rota de pacientes
    if (capturedImages.length >= 1) {
      const urlContainsPacientes = window.location.pathname.includes("/pacientes");
      if (!urlContainsPacientes) {
        toast.error("É necessário selecionar o paciente para poder subir múltiplas fotos de uma única vez");
        return;
      }
    }

    setIsCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(video, 0, 0);
      const blob = await new Promise<Blob | null>(resolve => 
        canvas.toBlob(resolve, "image/jpeg", 0.9)
      );

      if (blob) {
        const file = new File([blob], `camera_capture_${Date.now()}.jpg`, { type: "image/jpeg" });
        setCapturedImages(prev => [...prev, file]);
        toast.success("Foto capturada!");
      }
    } catch (err) {
      console.error("Erro ao capturar foto:", err);
      toast.error("Erro ao capturar foto");
    } finally {
      setIsCapturing(false);
    }
  }, [capturedImages.length]);

  const handleSwitch = useCallback(async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    setIsReady(false);
    await startCamera(newMode);
  }, [facingMode, startCamera]);

  const removeImage = useCallback((index: number) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
    toast.success("Foto removida");
  }, []);

  const handleConfirm = useCallback(() => {
    if (capturedImages.length === 0) {
      toast.error("Capture pelo menos uma foto");
      return;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    onCapture(capturedImages);
  }, [capturedImages, onCapture]);

  const handleClose = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    onCancel();
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3 bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Capturar Fotos</h2>
          {capturedImages.length > 0 && (
            <span className="text-sm text-muted-foreground bg-primary/10 px-2 py-1 rounded-full">
              {capturedImages.length} foto{capturedImages.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={handleClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-auto">
        {error ? (
          <div className="flex flex-col items-center justify-center max-w-md w-full bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Erro ao acessar câmera</h3>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <Button onClick={handleClose} variant="outline">Fechar</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-6 w-full max-w-md">
            <div className="relative w-full bg-black rounded-xl overflow-hidden shadow-2xl" style={{ aspectRatio: "3/4" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`}
              />
              {!isReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              )}
            </div>

            {capturedImages.length > 0 && (
              <div className="w-full">
                <h3 className="text-sm font-semibold mb-2">Fotos capturadas ({capturedImages.length}):</h3>
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
                  {capturedImages.map((file, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img src={URL.createObjectURL(file)} alt={`Captura ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        type="button"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">{index + 1}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-row gap-2 justify-center items-center">
              <Button onClick={handleClose} variant="outline" size="icon" className="h-12 w-12">
                <X className="h-5 w-5" />
              </Button>
              <Button onClick={handleSwitch} variant="outline" size="icon" className="h-12 w-12" disabled={isCapturing}>
                <RotateCcw className="h-5 w-5" />
              </Button>
              <Button onClick={handleCapture} size="icon" className="h-12 w-12" disabled={isCapturing || !isReady}>
                {isCapturing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CameraIcon className="h-5 w-5" />}
              </Button>
              {capturedImages.length > 0 && (
                <Button onClick={handleConfirm} size="icon" className="h-12 w-12 bg-primary text-primary-foreground">
                  <Check className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

